import { useState, useRef, useEffect } from "react";
import "./SpacerModal.scss";
import "../../../../components/ModalContent/ModalComponents.scss";
import { normalizeColor } from "../../../../utils/colors";
import ColorPicker from "../../../../components/ColorPicker/ColorPicker";
import { useTranslation } from "react-i18next";

/**
 * Add or edit a spacer - a titled, colored separator between list items
 * (e.g. one per supermarket in a shopping list).
 * Pass `spacer` to edit an existing one.
 */
function SpacerModal({ spacer, defaultColor, onSave, onCancel }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(spacer?.text || "");
  const [selectedColor, setSelectedColor] = useState(
    normalizeColor(spacer?.color || defaultColor),
  );
  const [showTitleError, setShowTitleError] = useState(false);
  const inputRef = useRef(null);

  // Set focus on input field when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 100); // 100ms delay to ensure DOM is ready
    return () => clearTimeout(timer);
  }, []);

  const handleSave = () => {
    if (title.trim()) {
      onSave(title.trim(), normalizeColor(selectedColor));
      setShowTitleError(false);
    } else {
      setShowTitleError(true);
    }
  };

  return (
    <div className="spacer-modal-content">
      <div className="modal-title">
        <div>{spacer ? t("lists.editSpacer") : t("lists.addSpacer")}</div>
      </div>
      <div className="modal-form">
        <div className="modal-form-title">{t("common.title")}</div>
        <input
          ref={inputRef}
          type="text"
          className="modal-text-input"
          placeholder={t("lists.spacerPlaceholder")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (showTitleError && e.target.value.trim()) {
              setShowTitleError(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            }
          }}
        />
        {showTitleError && (
          <div className="modal-helper-text">
            {t("lists.modals.titleRequired")}
          </div>
        )}

        <div className="modal-form-title">{t("common.color")}</div>
        <div className="color-picker-wrapper">
          <ColorPicker
            selectedColor={selectedColor}
            onColorSelect={setSelectedColor}
          />
        </div>
        <div className="modal-form-buttons">
          <button className="modal-button cancel" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button className="modal-button save" onClick={handleSave}>
            {spacer ? t("common.update") : t("common.add")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SpacerModal;
